import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  Box,
  Chip,
  Divider,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Payment as PaymentIcon,
  ContentCopy as CopyIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { gatewayService, Gateway } from '../../services/gatewayService';
import EscritorioSelect from '../selects/EscritorioSelect';
import env from '../../utils/env';

type GatewayType = 'asaas' | 'iugu' | 'efi' | 'sicoob';

interface GatewayFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (gatewayData: GatewayFormData) => Promise<void>;
  gatewayId?: number | null;
  loading?: boolean;
}

export interface GatewayFormData {
  nome: string;
  tipo: GatewayType;
  ambiente: 'sandbox' | 'producao';
  ativo: boolean;
  is_default: boolean;
  configuracoes: Record<string, any>;
  emitir_nfcom_auto: boolean;
  transmitir_nfcom_auto: boolean;
  escritorio_id: number | null;
  email_notificacoes: string;
  webhook_url: string;
}

interface FormErrors {
  nome?: string;
  tipo?: string;
  ambiente?: string;
  api_token?: string;
  client_id?: string;
  client_secret?: string;
  chave_pix?: string;
  certificado_path?: string;
  num_agencia?: string;
  num_conta?: string;
  nome_cedente?: string;
  cpf_cnpj?: string;
  local_pgto?: string;
  instrucoes?: string;
  email_notificacoes?: string;
}

const initialFormData: GatewayFormData = {
  nome: '',
  tipo: 'asaas',
  ambiente: 'sandbox',
  ativo: true,
  is_default: false,
  configuracoes: {},
  emitir_nfcom_auto: false,
  transmitir_nfcom_auto: false,
  escritorio_id: null,
  email_notificacoes: '',
  webhook_url: '',
};

const GatewayForm: React.FC<GatewayFormProps> = ({
  open,
  onClose,
  onSubmit,
  gatewayId,
  loading = false,
}) => {
  const [formData, setFormData] = useState<GatewayFormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string>('');
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [showAsaasHelp, setShowAsaasHelp] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const isEditing = !!gatewayId;

  // Carregar dados do gateway para edição
  useEffect(() => {
    const fetchGatewayData = async () => {
      if (isEditing && gatewayId) {
        try {
          setLoadingData(true);
          const data = await gatewayService.getGatewayById(gatewayId);

          // Montar configurações a partir dos campos específicos do gateway
          const configs: Record<string, any> = {};

          if (data.api_token) configs.api_token = data.api_token;
          if (data.client_id) configs.client_id = data.client_id;
          if (data.client_secret) configs.client_secret = data.client_secret;
          if (data.chave_pix) configs.chave_pix = data.chave_pix;
          if (data.certificado_path) configs.certificado_path = data.certificado_path;
          if (data.num_agencia) configs.num_agencia = data.num_agencia;
          if (data.num_conta) configs.num_conta = data.num_conta;
          if (data.nome_cedente) configs.nome_cedente = data.nome_cedente;
          if (data.cpf_cnpj) configs.cpf_cnpj = formatCpfCnpj(data.cpf_cnpj);
          if (data.local_pgto) configs.local_pgto = data.local_pgto;
          if (data.instrucoes) configs.instrucoes = data.instrucoes;

          if (data.configuracoes_extras) {
            Object.assign(configs, data.configuracoes_extras);
          }

          setFormData({
            nome: data.nome,
            tipo: data.tipo,
            ambiente: data.ambiente,
            ativo: data.ativo,
            is_default: data.is_default,
            configuracoes: configs,
            emitir_nfcom_auto: data.emitir_nfcom_auto,
            transmitir_nfcom_auto: data.transmitir_nfcom_auto,
            escritorio_id: data.escritorio_id || null,
            email_notificacoes: data.email_notificacoes || '',
            webhook_url: data.webhook_url || '',
          });
        } catch (error: any) {
          console.error('Erro ao buscar dados do gateway:', error);
          setSubmitError('Erro ao buscar dados do gateway para edição.');
        } finally {
          setLoadingData(false);
        }
      } else {
        setFormData({
          ...initialFormData,
          webhook_url: `${env.API_URL}/api/webhooks/${initialFormData.tipo}`
        });
      }
    };

    if (open) {
      fetchGatewayData();
    }
    setErrors({});
    setSubmitError('');
  }, [gatewayId, isEditing, open]);

  const handleInputChange = (field: keyof GatewayFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    // Limpar erro do campo
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field as keyof FormErrors]: undefined }));
    }
  };

  const handleSelectChange = (field: keyof GatewayFormData) => (event: any) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    // Limpar configurações ao mudar tipo
    if (field === 'tipo') {
      setFormData(prev => ({
        ...prev,
        configuracoes: {},
        webhook_url: !isEditing ? `${env.API_URL}/api/webhooks/${value}` : prev.webhook_url
      }));
    }

    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field as keyof FormErrors]: undefined }));
    }
  };

  const handleSwitchChange = (field: keyof GatewayFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;
    setFormData(prev => ({ ...prev, [field]: checked }));
  };

  const updateConfig = (key: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      configuracoes: {
        ...prev.configuracoes,
        [key]: value,
      },
    }));

    // Limpar erro do campo de configuração
    if (errors[key as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [key as keyof FormErrors]: undefined }));
    }
  };

  const togglePasswordVisibility = (field: string) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validações básicas
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (formData.tipo === 'asaas' && !formData.email_notificacoes?.trim()) {
      newErrors.email_notificacoes = 'E-mail para notificações é obrigatório para o Asaas';
    } else if (formData.email_notificacoes && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_notificacoes)) {
      newErrors.email_notificacoes = 'E-mail inválido';
    }

    if (!formData.tipo) {
      newErrors.tipo = 'Tipo é obrigatório';
    }

    // Validações específicas por tipo
    const configs = formData.configuracoes || {};

    switch (formData.tipo) {
      case 'asaas':
      case 'iugu':
        if (!configs.api_token?.trim()) {
          newErrors.api_token = 'API Key é obrigatória';
        }
        break;

      case 'efi':
        if (!configs.client_id?.trim()) {
          newErrors.client_id = 'Client ID é obrigatório';
        }
        if (!configs.client_secret?.trim()) {
          newErrors.client_secret = 'Client Secret é obrigatório';
        }
        break;

      case 'sicoob':
        if (!configs.client_id?.trim()) {
          newErrors.client_id = 'Client ID é obrigatório';
        }
        if (!configs.client_secret?.trim()) {
          newErrors.client_secret = 'Senha do Certificado é obrigatória';
        }
        break;
    }

    // Validações campos bancários obrigatórios
    if (!configs.nome_cedente || !configs.nome_cedente.trim()) {
      newErrors.nome_cedente = 'Nome do cedente é obrigatório';
    }
    if (!configs.cpf_cnpj || !configs.cpf_cnpj.trim()) {
      newErrors.cpf_cnpj = 'CPF/CNPJ do cedente é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error: any) {
      console.error('Erro no formulário:', error);

      const apiErrors = error.response?.data?.errors;
      const apiMessage = error.response?.data?.message;

      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        const fieldErrors: FormErrors = {};
        const messages: string[] = [];

        apiErrors.forEach((err: any) => {
          if (typeof err === 'object' && err.param) {
            fieldErrors[err.param as keyof FormErrors] = err.msg || err.message;
            messages.push(err.msg || err.message);
          } else if (typeof err === 'string') {
            messages.push(err);
          }
        });

        if (Object.keys(fieldErrors).length > 0) {
          setErrors(prev => ({ ...prev, ...fieldErrors }));
        }

        setSubmitError(messages.join(' | ') || apiMessage || 'Erro ao salvar gateway');
      } else {
        setSubmitError(apiMessage || error.message || 'Erro ao salvar gateway');
      }
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    setSubmitError('');
    setShowPasswords({});
    onClose();
  };

  // Função para formatar CPF/CNPJ de forma progressiva
  const formatCpfCnpj = (value: string) => {
    if (!value) return '';
    const clean = value.replace(/\D/g, '').slice(0, 14);
    if (clean.length <= 11) {
      return clean
        .replace(/^(\d{3})(\d)/, '$1.$2')
        .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
    } else {
      return clean
        .replace(/^(\d{2})(\d)/, '$1.$2')
        .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
        .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5');
    }
  };

  // Renderizar campos de configuração específicos por tipo
  const renderConfigurationFields = () => {
    const configs = formData.configuracoes || {};

    switch (formData.tipo) {
      case 'asaas':
        return (
          <TextField
            fullWidth
            label="API Key"
            type={showPasswords.api_token ? 'text' : 'password'}
            value={configs.api_token || ''}
            onChange={(e) => updateConfig('api_token', e.target.value)}
            required
            size="small"
            error={!!errors.api_token}
            //helperText={errors.api_token || 'Chave de API fornecida pelo Asaas'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowAsaasHelp(true)}
                    size="small"
                    title="Como obter a API Key?"
                    sx={{ mr: 1 }}
                  >
                    <HelpIcon fontSize="small" color="primary" />
                  </IconButton>
                  <IconButton
                    onClick={() => togglePasswordVisibility('api_token')}
                    edge="end"
                  >
                    {showPasswords.api_token ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        );

      case 'iugu':
        return (
          <TextField
            fullWidth
            label="Token de API"
            type={showPasswords.api_token ? 'text' : 'password'}
            value={configs.api_token || ''}
            onChange={(e) => updateConfig('api_token', e.target.value)}
            required
            size="small"
            margin="normal"
            error={!!errors.api_token}
            helperText={errors.api_token || 'Token de API fornecido pelo Iugu'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => togglePasswordVisibility('api_token')}
                    edge="end"
                  >
                    {showPasswords.api_token ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        );

      case 'efi':
        return (
          <>
            <TextField
              fullWidth
              label="Client ID"
              value={configs.client_id || ''}
              onChange={(e) => updateConfig('client_id', e.target.value)}
              required
              size="small"
              margin="normal"
              error={!!errors.client_id}
              helperText={errors.client_id || 'Identificador do cliente no EFI/Gerencianet'}
            />
            <TextField
              fullWidth
              label="Client Secret"
              type={showPasswords.client_secret ? 'text' : 'password'}
              value={configs.client_secret || ''}
              onChange={(e) => updateConfig('client_secret', e.target.value)}
              required
              size="small"
              margin="normal"
              error={!!errors.client_secret}
              helperText={errors.client_secret || 'Chave secreta do cliente'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => togglePasswordVisibility('client_secret')}
                      edge="end"
                    >
                      {showPasswords.client_secret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </>
        );

      case 'sicoob':
        return (
          <>
            <TextField
              fullWidth
              label="Client ID"
              value={configs.client_id || ''}
              onChange={(e) => updateConfig('client_id', e.target.value)}
              required
              size="small"
              margin="normal"
              error={!!errors.client_id}
              helperText={errors.client_id || 'Client ID OAuth2 do Sicoob'}
            />
            <TextField
              fullWidth
              label="Senha do Certificado P12"
              type={showPasswords.client_secret ? 'text' : 'password'}
              value={configs.client_secret || ''}
              onChange={(e) => updateConfig('client_secret', e.target.value)}
              required
              size="small"
              margin="normal"
              error={!!errors.client_secret}
              helperText={errors.client_secret || 'Senha do certificado digital .pfx/.p12'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => togglePasswordVisibility('client_secret')}
                      edge="end"
                    >
                      {showPasswords.client_secret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Caminho do Certificado P12"
              value={configs.certificado_path || ''}
              onChange={(e) => updateConfig('certificado_path', e.target.value)}
              size="small"
              margin="normal"
              helperText="Caminho completo do arquivo .pfx/.p12 no servidor"
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon />
          {isEditing ? 'Editar Gateway' : 'Novo Gateway'}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loadingData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <Typography variant="body2" color="text.secondary">Carregando dados...</Typography>
          </Box>
        ) : (
          <>
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}

            <Grid container spacing={1} sx={{ mt: 1 }}>

              {/* Escritório */}
              <Grid item xs={12}>
                <EscritorioSelect
                  value={formData.escritorio_id}
                  onChange={(val) => setFormData(prev => ({ ...prev, escritorio_id: val as number | null }))}
                  size="small"
                  label="Escritório Responsável"
                  placeholder="Selecione o escritório para este gateway"
                />
              </Grid>

              {/* Nome do Gateway */}
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  label="Nome do Gateway"
                  value={formData.nome}
                  onChange={handleInputChange('nome')}
                  required
                  error={!!errors.nome}
                  //helperText={errors.nome || 'Nome identificador do gateway'}
                  size="small"
                />
              </Grid>
              {/* Tipo do Gateway */}
              <Grid item xs={12} md={7}>
                <FormControl fullWidth required error={!!errors.tipo}>
                  <InputLabel>Tipo do Gateway</InputLabel>
                  <Select
                    value={formData.tipo}
                    label="Tipo do Gateway"
                    onChange={handleSelectChange('tipo')}
                    size="small"
                  >
                    <MenuItem value="asaas">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Asaas
                        <Chip label="PIX + Boleto" size="small" color="success" />
                      </Box>
                    </MenuItem>
                    <MenuItem value="iugu">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Iugu
                        <Chip label="PIX + Boleto" size="small" color="primary" />
                      </Box>
                    </MenuItem>
                    <MenuItem value="efi">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        EFI/Gerencianet
                        <Chip label="PIX + Boleto" size="small" color="secondary" />
                      </Box>
                    </MenuItem>
                    <MenuItem value="sicoob">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Sicoob
                        <Chip label="PIX" size="small" sx={{ bgcolor: '#003641', color: '#fff' }} />
                      </Box>
                    </MenuItem>
                  </Select>
                  {errors.tipo && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.tipo}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {formData.tipo && (
                <Grid item xs={12} md={8}>
                  <TextField
                    value={formData.webhook_url}
                    size="small"
                    fullWidth
                    label="Webhook"
                    InputProps={{
                      readOnly: true,
                      sx: {
                        fontFamily: 'monospace',
                        fontSize: '0.95rem',
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(formData.webhook_url);
                            }}
                            title="Copiar link"
                            edge="end"
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              )}

              {/* Ambiente */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Ambiente</InputLabel>
                  <Select
                    value={formData.ambiente}
                    label="Ambiente"
                    onChange={handleSelectChange('ambiente')}
                    size="small"
                  >
                    <MenuItem value="sandbox">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Sandbox
                        <Chip label="Testes" size="small" color="warning" />
                      </Box>
                    </MenuItem>
                    <MenuItem value="producao">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        Produção
                        <Chip label="Real" size="small" color="error" />
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Status Ativo */}
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.ativo}
                      onChange={handleSwitchChange('ativo')}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Gateway ativo
                      <Chip
                        label={formData.ativo ? 'Ativo' : 'Inativo'}
                        size="small"
                        color={formData.ativo ? 'success' : 'default'}
                      />
                    </Box>
                  }
                />
              </Grid>

              {/* Gateway Padrão */}
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_default}
                      onChange={handleSwitchChange('is_default')}
                      color="warning"
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Gateway padrão
                      <Chip
                        label={formData.is_default ? 'Padrão' : 'Não'}
                        size="small"
                        color={formData.is_default ? 'warning' : 'default'}
                      />
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.emitir_nfcom_auto}
                      onChange={handleSwitchChange('emitir_nfcom_auto')}
                      color="primary"
                      size="small"
                    />
                  }
                  label="Emitir NFCom automaticamente"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.transmitir_nfcom_auto}
                      onChange={handleSwitchChange('transmitir_nfcom_auto')}
                      color="primary"
                      size="small"
                    />
                  }
                  label="Transmitir NFCom automaticamente"
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ mt: 3 }} />
              </Grid>

              {/* Configurações específicas do gateway */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 0 }}>
                  Configurações do {gatewayService.getGatewayDisplayName(formData.tipo)}
                </Typography>
                {renderConfigurationFields()}
              </Grid>

              {/* Campos bancários comuns */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                  Configurações Bancárias
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Agência"
                      value={formData.configuracoes?.num_agencia || ''}
                      onChange={(e) => updateConfig('num_agencia', e.target.value)}
                      error={!!errors.num_agencia}
                      //helperText={errors.num_agencia || 'Número da agência bancária'}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Conta"
                      value={formData.configuracoes?.num_conta || ''}
                      onChange={(e) => updateConfig('num_conta', e.target.value)}
                      error={!!errors.num_conta}
                      //helperText={errors.num_conta || 'Número da conta bancária'}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Chave PIX"
                      value={formData.configuracoes?.chave_pix || ''}
                      onChange={(e) => updateConfig('chave_pix', e.target.value)}
                      error={!!errors.chave_pix}
                      //helperText={errors.chave_pix || 'Chave PIX para recebimento'}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="CPF/CNPJ do Cedente"
                      value={formData.configuracoes?.cpf_cnpj || ''}
                      onChange={(e) => updateConfig('cpf_cnpj', formatCpfCnpj(e.target.value))}
                      required
                      error={!!errors.cpf_cnpj}
                      helperText={errors.cpf_cnpj}
                      inputProps={{ maxLength: 18 }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="Nome do Cedente"
                      value={formData.configuracoes?.nome_cedente || ''}
                      onChange={(e) => updateConfig('nome_cedente', e.target.value)}
                      required
                      error={!!errors.nome_cedente}
                      helperText={errors.nome_cedente}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={7} md={7}>
                    <TextField
                      fullWidth
                      label="Local de Pagamento"
                      value={formData.configuracoes?.local_pgto || ''}
                      onChange={(e) => updateConfig('local_pgto', e.target.value)}
                      error={!!errors.local_pgto}
                      //helperText={errors.local_pgto || 'Local preferencial para pagamento do boleto'}
                      size="small"
                    />
                  </Grid>
                  {/* E-mail de Notificações */}
                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      label="E-mail para Notificações"
                      value={formData.email_notificacoes}
                      onChange={handleInputChange('email_notificacoes')}
                      placeholder="exemplo@email.com"
                      size="small"
                      error={!!errors.email_notificacoes}
                      required={formData.tipo === 'asaas'}
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <TextField
                      fullWidth
                      label="Instruções do Boleto"
                      value={formData.configuracoes?.instrucoes || ''}
                      onChange={(e) => updateConfig('instrucoes', e.target.value)}
                      error={!!errors.instrucoes}
                      //helperText={errors.instrucoes || 'Instruções adicionais que aparecerão no boleto'}
                      multiline
                      minRows={2}
                      maxRows={5}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Grid>

            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
      <Dialog
        open={showAsaasHelp}
        onClose={() => setShowAsaasHelp(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HelpIcon color="primary" />
            Como obter sua API Key do Asaas
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            Siga os passos abaixo para gerar sua chave de API no Asaas:
          </Typography>
          <Box component="ol" sx={{ pl: 2 }}>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Acesse o painel do <strong>Asaas</strong> com sua conta.
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                No canto superior direito, clique no seu nome ou na engrenagem de <strong>Configurações</strong>.
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                No menu lateral esquerdo, selecione a opção <strong>Integrações</strong>.
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Na página de Integrações, localize a seção <strong>Chaves de API</strong>.
              </Typography>
            </li>
            <li>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Clique em <strong>Gerar nova chave</strong> ou copie uma chave existente.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Cole a chave obtida no campo <strong>API Key</strong> deste formulário.
              </Typography>
            </li>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Certifique-se de estar copiando a chave do ambiente correto (Produção ou Sandbox) dependendo do que selecionou no formulário.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAsaasHelp(false)} color="primary" variant="contained">
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default GatewayForm; 